import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import FaceIcon from '@mui/icons-material/Face';
import LightModeIcon from '@mui/icons-material/LightMode';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useCamera } from '../../../hooks/useCamera';
import { useFaceDetection } from '../../../hooks/useFaceDetection';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface LivenessGuideProps {
  // Llamado cuando el usuario confirma que está listo y hace clic en "Iniciar"
  onReady: () => void;
  // Número de intento actual (para mostrar "Intento X de 3" en reintentos)
  attemptNumber?: number;
  // Cuando es true, omite la detección de rostro y habilita el botón en cuanto la cámara esté lista.
  // Usar en reintentos: tras una sesión de liveness, el runtime WASM de TF.js puede quedar en estado
  // inconsistente causando que face-api.js lance excepciones que escapan todos los catch handlers.
  skipFaceDetection?: boolean;
}

// Estructura de cada instrucción de la guía
interface GuideStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

// Intervalo en ms entre cada verificación de rostro en el preview de cámara
const FACE_CHECK_INTERVAL_MS = 1200;

const GUIDE_STEPS: GuideStep[] = [
  {
    icon: <LightModeIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
    title: 'Buena iluminación',
    description: 'Sitúate en un lugar bien iluminado, de frente a la luz.',
  },
  {
    icon: <FaceIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    title: 'Rostro al centro',
    description: 'Centra tu rostro dentro del óvalo y mantén la cámara a la altura de los ojos.',
  },
  {
    icon: <RemoveRedEyeIcon sx={{ fontSize: 32, color: 'success.main' }} />,
    title: 'Sigue las instrucciones',
    description: 'Realiza el movimiento que indique la pantalla en los próximos pasos.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pantalla de instrucciones previa a la prueba de vida.
 *
 * Muestra:
 *   1. Tres tarjetas de instrucciones (iluminación, encuadre, movimiento)
 *   2. Preview de cámara frontal con indicador de detección de rostro
 *   3. Botón "Iniciar prueba de vida" (deshabilitado si no hay rostro detectado)
 *
 * La detección de rostro es un pre-check opcional — si face-api.js no logra cargar
 * los modelos, el botón permanece habilitado y el flujo continúa sin bloquear al usuario.
 */
export function LivenessGuide({ onReady, attemptNumber, skipFaceDetection = false }: LivenessGuideProps) {
  const { videoRef, isReady, error: cameraError, start } = useCamera({
    facingMode: 'user',
    width: 320,
    height: 240,
  });

  const { isModelLoaded, isModelLoading, detectFace } = useFaceDetection();

  // null = no verificado aún | true = rostro detectado | false = no detectado
  const [faceDetected, setFaceDetected] = useState<boolean | null>(null);

  // Referencia al interval de detección para poder cancelarlo al desmontar
  const detectionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Inicia la cámara frontal al montar el componente
  useEffect(() => {
    void start();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inicia el loop de detección de rostro cuando la cámara y el modelo están listos.
  // Omitido en reintentos (skipFaceDetection=true): tras una sesión de liveness el
  // backend WASM de TF.js puede quedar en estado inconsistente y face-api.js lanzaría
  // excepciones que escapan todos los catch handlers, bloqueando el botón indefinidamente.
  useEffect(() => {
    if (skipFaceDetection || !isReady || !isModelLoaded || !videoRef.current) return;

    const runDetection = async () => {
      if (!videoRef.current) return;
      const detected = await detectFace(videoRef.current);
      setFaceDetected(detected);
    };

    void runDetection();

    detectionIntervalRef.current = setInterval(() => {
      void runDetection();
    }, FACE_CHECK_INTERVAL_MS);

    return () => {
      if (detectionIntervalRef.current !== null) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };
  }, [skipFaceDetection, isReady, isModelLoaded, detectFace, videoRef]);

  // true cuando la carga del modelo terminó con error (404 u otro — fail open)
  const modelLoadFailed = !isModelLoaded && !isModelLoading;

  // El botón se habilita si: no hay error de cámara, la cámara está lista, y:
  //   - skipFaceDetection=true (reintento): habilitar en cuanto la cámara esté lista
  //   - rostro detectado por face-api.js
  //   - o el modelo no cargó (fail open)
  const canStart = !cameraError && isReady && (skipFaceDetection || faceDetected === true || modelLoadFailed);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Encabezado */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Prueba de vida
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verifica que eres una persona real frente a la cámara. El proceso toma menos de 30 segundos.
        </Typography>
      </Box>

      {/* Chip de reintento */}
      {attemptNumber !== undefined && attemptNumber > 0 && (
        <Chip
          label={`Intento ${attemptNumber + 1} de 3`}
          color="warning"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      {/* Tarjetas de instrucciones */}
      <Stack spacing={1.5}>
        {GUIDE_STEPS.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>{step.icon}</Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Preview de cámara frontal */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Vista previa de cámara
        </Typography>

        {cameraError ? (
          <Alert severity="warning" sx={{ width: '100%' }}>
            {cameraError.message}
          </Alert>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: 280,
              height: 210,
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: '#111',
              border: '2px solid',
              borderColor: faceDetected === true ? 'success.main' : 'divider',
              transition: 'border-color 0.3s',
            }}
          >
            {/* Elemento de video — espejado para que se vea natural al usuario */}
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)', // espejo para vista frontal natural
                display: isReady ? 'block' : 'none',
              }}
            />

            {/* Spinner mientras la cámara carga */}
            {!isReady && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} color="inherit" sx={{ color: 'grey.400' }} />
              </Box>
            )}

            {/* Óvalo guía para el encuadre del rostro */}
            {isReady && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 160,
                    borderRadius: '50%',
                    border: '2px dashed',
                    borderColor: faceDetected === true ? 'success.light' : 'rgba(255,255,255,0.5)',
                    transition: 'border-color 0.3s',
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Indicador de detección de rostro */}
        {isReady && !cameraError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {skipFaceDetection ? (
              // En reintentos se omite la detección — el botón ya está habilitado
              <>
                <CheckCircleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Encuadra tu rostro y pulsa Iniciar
                </Typography>
              </>
            ) : isModelLoading || (faceDetected === null && !modelLoadFailed) ? (
              // Modelo cargando o primera detección aún pendiente
              <>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Verificando encuadre...
                </Typography>
              </>
            ) : faceDetected === true ? (
              // Modelo cargado y rostro detectado correctamente
              <>
                <CheckCircleIcon fontSize="small" color="success" />
                <Typography variant="caption" color="success.main">
                  Rostro detectado — estás listo
                </Typography>
              </>
            ) : modelLoadFailed ? (
              // Modelo no disponible — degradar visualmente; el botón sigue habilitado
              <>
                <CheckCircleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Encuadra tu rostro y pulsa Iniciar
                </Typography>
              </>
            ) : (
              // Modelo cargado pero no detectó rostro en el frame actual
              <>
                <WarningAmberIcon fontSize="small" color="warning" />
                <Typography variant="caption" color="warning.main">
                  Centra tu rostro dentro del óvalo
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Botón de inicio */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!canStart}
        onClick={onReady}
        startIcon={<FaceIcon />}
      >
        Iniciar prueba de vida
      </Button>

      {/* Aviso de privacidad */}
      <Typography variant="caption" color="text.disabled" align="center">
        Las imágenes capturadas se usan únicamente para verificar tu identidad y no se
        almacenan con fines comerciales.
      </Typography>
    </Box>
  );
}
